import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Megaphone, ChevronLeft, ChevronRight } from 'lucide-react';
import { getActiveAnnouncements } from '../api';

const DISMISSED_KEY = 'dismissed_announcements';

function getDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); }
  catch { return []; }
}

function setDismissed(ids) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
}

function MarqueeText({ text, onClick, hasLink }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [duration, setDuration] = useState(18);

  useEffect(() => {
    const check = () => {
      if (!containerRef.current || !textRef.current) return;
      const cw = containerRef.current.offsetWidth;
      const tw = textRef.current.scrollWidth;
      const scroll = tw > cw - 10;
      setShouldScroll(scroll);
      // ~60px/s
      setDuration(scroll ? Math.max(8, Math.round(tw / 60)) : 0);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [text]);

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      style={{
        flex: 1,
        overflow: 'hidden',
        cursor: hasLink ? 'pointer' : 'default',
        minWidth: 0,
        position: 'relative',
        textAlign: shouldScroll ? 'left' : 'center',
      }}
    >
      <style>{`
        @keyframes marquee-scroll {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .marquee-text { display: inline-block; }
        .marquee-text.scrolling {
          animation: marquee-scroll var(--marquee-dur, 18s) linear infinite;
        }
        .marquee-text.scrolling:hover { animation-play-state: paused; }
      `}</style>
      <p
        ref={textRef}
        className={`marquee-text${shouldScroll ? ' scrolling' : ''}`}
        style={{
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.4,
          margin: 0,
          whiteSpace: 'nowrap',
          '--marquee-dur': `${duration}s`,
        }}
      >
        {text}
        {hasLink && (
          <span style={{ marginLeft: 10, fontSize: 11, opacity: 0.75, fontWeight: 400 }}>
            En savoir plus →
          </span>
        )}
      </p>
    </div>
  );
}

export function AnnouncementBanner() {
  const [announcements, setAnnouncements] = useState([]);
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await getActiveAnnouncements();
        if (cancelled) return;
        const dismissed = getDismissed();
        const visible = (res.data || []).filter(a => !dismissed.includes(a.id));
        setAnnouncements(visible);
        setIndex(0);
      } catch { /* silencieux */ }
    }
    load();
    const timer = setInterval(load, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (announcements.length === 0) return null;

  const current = announcements[index];

  const dismiss = (e) => {
    e.stopPropagation();
    const dismissed = getDismissed();
    if (!dismissed.includes(current.id)) setDismissed([...dismissed, current.id]);
    const next = announcements.filter(a => a.id !== current.id);
    setAnnouncements(next);
    setIndex(i => Math.min(i, next.length - 1));
  };

  const handleClick = () => {
    if (current.lien) {
      if (current.lien.startsWith('http')) window.open(current.lien, '_blank', 'noopener');
      else navigate(current.lien);
    }
  };

  const prev = (e) => { e.stopPropagation(); setIndex(i => (i - 1 + announcements.length) % announcements.length); };
  const next = (e) => { e.stopPropagation(); setIndex(i => (i + 1) % announcements.length); };

  return (
    <div
      data-testid="announcement-banner"
      style={{
        background: 'linear-gradient(90deg, #1E40AF 0%, #3B82F6 100%)',
        color: 'white',
        padding: '7px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
        minHeight: 38,
        overflow: 'hidden',
      }}
    >
      <Megaphone size={14} style={{ flexShrink: 0, opacity: 0.85 }} />

      <MarqueeText
        text={current.message}
        onClick={handleClick}
        hasLink={!!current.lien}
      />

      {announcements.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <button onClick={prev} data-testid="announcement-prev"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center' }}>
            <ChevronLeft size={13} />
          </button>
          <span style={{ fontSize: 11, opacity: 0.7, minWidth: 30, textAlign: 'center' }}>
            {index + 1}/{announcements.length}
          </span>
          <button onClick={next} data-testid="announcement-next"
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center' }}>
            <ChevronRight size={13} />
          </button>
        </div>
      )}

      <button
        onClick={dismiss}
        data-testid="announcement-dismiss"
        title="Fermer définitivement"
        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 4, padding: 4, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        <X size={13} />
      </button>
    </div>
  );
}
